import PropTypes from 'prop-types'
import * as api from '@owl-nest/api-client/latest'

export const statsShape = PropTypes.shape({
  created_projects_online_count: PropTypes.number,
  followed_projects_count: PropTypes.number,
  projects_created_count: PropTypes.number,
  projects_currently_funding_count: PropTypes.number,
  projects_failed_count: PropTypes.number,
  projects_orders_count: PropTypes.number,
  projects_succeeded_count: PropTypes.number,
  proposals_created_count: PropTypes.number,
  proposals_refused_count: PropTypes.number,
  proposals_validated_count: PropTypes.number,
  supported_projects_count: PropTypes.number,
})

export const userShape = PropTypes.shape({
  absolute_url: PropTypes.string,
  avatar: PropTypes.object,
  firstName: PropTypes.string,
  first_name: PropTypes.string,
  fullname: PropTypes.string,
  getAvatar: PropTypes.func,
  id: PropTypes.number,
  isAuthenticated: PropTypes.bool,
  is_authenticated: PropTypes.bool,
  is_staff: PropTypes.bool,
  last_name: PropTypes.string,
  name: PropTypes.string,
  nb_projects: PropTypes.number,
  project_list_url: PropTypes.string,
  screenname: PropTypes.string,
  settings_url: PropTypes.string,
  shortname: PropTypes.string,
  transactions_url: PropTypes.string,
})

export type UserShape = {
  absolute_url: string,
  avatar: unknown,
  contact_lang?: string,
  firstName: string,
  first_name: string,
  fullname: string,
  getAvatar: (s: string) => { url: string },
  id: number,
  isAuthenticated: boolean,
  is_authenticated: boolean,
  is_staff: boolean,
  last_login: string,
  last_name: string,
  lang?: string,
  name: string,
  nb_projects: number,
  project_list_url: string,
  screenname: string,
  settings_url: string,
  shortname: string,
  stats?: {
    projects_created_count: number,
    proposals_created_count: number,
    projects_succeeded_count: number,
    proposals_validated_count: number,
  },
  transactions_url: string,
}

export const langShape = PropTypes.shape({
  en: PropTypes.string,
  es: PropTypes.string,
  fr: PropTypes.string,
  it: PropTypes.string,
  nl: PropTypes.string,
})

export const categoryShape = PropTypes.shape({
  absolute_url: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  name: langShape,
  position: PropTypes.number.isRequired,
  slug: PropTypes.string.isRequired,
})

export const partnershipsShape = PropTypes.shape({
  id: PropTypes.number,
  is_default: PropTypes.bool,
  is_support: PropTypes.bool,
  partner: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    slug: PropTypes.string,
    url: PropTypes.string,
    user_id: PropTypes.number,
  }),
  project_id: PropTypes.number,
})

export const urlsShape = PropTypes.shape({
  web: PropTypes.shape({
    checkout: PropTypes.string,
    edit: PropTypes.shape({
      address: PropTypes.string,
      main: PropTypes.string,
      media: PropTypes.string,
      news: PropTypes.shape({
        add: PropTypes.string,
        index: PropTypes.string,
      }),
      orders: PropTypes.string,
      products: PropTypes.string,
      rib: PropTypes.string,
      ticket: PropTypes.string,
    }),
    detail: PropTypes.string,
  }),
})

export const projectShape = PropTypes.shape({
  get: PropTypes.func,
  manager: userShape,
  props: PropTypes.shape({
    absolute_url: PropTypes.string,
    amountLeft: PropTypes.number,
    amount_raised: PropTypes.number,
    analytics_count: PropTypes.number,
    comments_count: PropTypes.number,
    committed: PropTypes.number,
    countdown: PropTypes.number,
    country: PropTypes.string,
    currency: PropTypes.string,
    date_end: PropTypes.string,
    date_start: PropTypes.string,
    description: langShape,
    description_funding: langShape,
    description_yourself: langShape,
    discussions_thread_id: PropTypes.number,
    finished: PropTypes.bool,
    getMainImage: PropTypes.func,
    getUrl: PropTypes.func,
    goal: PropTypes.number,
    goal_raised: PropTypes.bool,
    id: PropTypes.number,
    isFailed: PropTypes.bool,
    isFinished: PropTypes.bool,
    isFunded: PropTypes.bool,
    isPresale: PropTypes.bool,
    isSuccess: PropTypes.bool,
    is_cancelled: PropTypes.bool,
    is_online: PropTypes.bool,
    lang: PropTypes.string,
    location: PropTypes.shape({
      city: PropTypes.string,
      country: PropTypes.string,
      full_name: PropTypes.string,
      name: PropTypes.string,
      region: PropTypes.string,
    }),
    lowest_contribution_amount: PropTypes.number,
    manager: userShape,
    name: langShape,
    nb_products_sold: PropTypes.number,
    news_count: PropTypes.number,
    note: PropTypes.string,
    orders_count: PropTypes.number,
    owner: userShape,
    partnerships: PropTypes.arrayOf(partnershipsShape),
    percent: PropTypes.number,
    required_personal_id_number: PropTypes.bool,
    resource_uri: PropTypes.string,
    slug: PropTypes.string,
    sponsorships_count: PropTypes.number,
    status: PropTypes.string,
    subtitle: langShape,
    supporters_count: PropTypes.number,
    timeLeft: PropTypes.string,
    timezone: PropTypes.string,
    type: PropTypes.string,
    urls: PropTypes.shape({
      urlsShape,
    }),
    video: PropTypes.shape({
      author_name: PropTypes.string,
      author_url: PropTypes.string,
      height: PropTypes.number,
      html: PropTypes.string,
      id: PropTypes.number,
      language: PropTypes.string,
      provider_name: PropTypes.string,
      provider_url: PropTypes.string,
      thumbnail_height: PropTypes.number,
      thumbnail_url: PropTypes.string,
      thumbnail_width: PropTypes.number,
      title: PropTypes.string,
      type: PropTypes.string,
      url: PropTypes.string,
      version: PropTypes.string,
      width: PropTypes.number,
    }),
    visible: PropTypes.bool,
  }),
  user: userShape,
})

type Exemple = {
  main_image: {
    versions: {
      small: {
        url: string,
      },
    },
  },
}
export type ProjectShape = {
  get: (name: string, tag?: api.TagBase) => string,
  isLoadingProject: boolean,
  isUpdatingManagerType: boolean,
  manager: UserShape,
  props: api.Project & {
    account: {
      status: string,
      edit_url: string,
    },
    countdown: number,
    currency: string,
    date_start: string,
    isFinished: boolean,
    isSelected: boolean,
    latest_news: string,
    billing_blocked: boolean,
    manager_type: 'specialist' | 'success',
    news_count: number,
    note: string,
    notes: {
      manager: string,
      billing: string,
    },
    percent: string,
    proposal: ProposalShape,
    recommendable: boolean,
    start_date: string,
    slug: string,
    timeLeft: number,
  },
  user: UserShape,
  validator: { fullname?: string | undefined },
}

export const symbolsShape = PropTypes.shape({
  AUD: PropTypes.shape({
    separator: PropTypes.string,
    symbol: PropTypes.string,
    trigram: PropTypes.string,
  }),
  CAD: PropTypes.shape({
    separator: PropTypes.string,
    symbol: PropTypes.string,
    trigram: PropTypes.string,
  }),
  CHF: PropTypes.shape({
    separator: PropTypes.string,
    symbol: PropTypes.string,
    trigram: PropTypes.string,
  }),
  DKK: PropTypes.shape({
    separator: PropTypes.string,
    symbol: PropTypes.string,
    trigram: PropTypes.string,
  }),
  EUR: PropTypes.shape({
    separator: PropTypes.string,
    symbol: PropTypes.string,
    trigram: PropTypes.string,
  }),
  GBP: PropTypes.shape({
    separator: PropTypes.string,
    symbol: PropTypes.string,
    trigram: PropTypes.string,
  }),
  NOK: PropTypes.shape({
    separator: PropTypes.string,
    symbol: PropTypes.string,
    trigram: PropTypes.string,
  }),
  NZD: PropTypes.shape({
    separator: PropTypes.string,
    symbol: PropTypes.string,
    trigram: PropTypes.string,
  }),
  SEK: PropTypes.shape({
    separator: PropTypes.string,
    symbol: PropTypes.string,
    trigram: PropTypes.string,
  }),
  USD: PropTypes.shape({
    separator: PropTypes.string,
    symbol: PropTypes.string,
    trigram: PropTypes.string,
  }),
})

export const proposalShape = PropTypes.shape({
  city: PropTypes.string,
  country: PropTypes.string,
  date_creation: PropTypes.string,
  description: PropTypes.string,
  goal_range: PropTypes.shape({
    max: PropTypes.number,
    min: PropTypes.number,
  }),
  id: PropTypes.number,
  legal_entity_type: PropTypes.string,
  partner: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    slug: PropTypes.string,
  }),
  phone_number: PropTypes.string,
  rewards_type: PropTypes.string,
  score: PropTypes.number,
  score_entries: PropTypes.shape({
    description: PropTypes.number,
    goal_range: PropTypes.number,
    legal_entity_type: PropTypes.number,
    links: PropTypes.number,
    rewards_type: PropTypes.number,
    user: PropTypes.number,
  }),
})

export type ProposalShape = {
  city: string,
  country: string,
  date_creation: string,
  description: string,
  goal_range: {
    max: number,
    min: number,
  },
  id: number,
  legal_entity_type: string,
  links: [
    {
      followers_count: number,
      id: number,
      url: string,
      views_count: number,
    },
  ],
  partner: {
    id: number,
    name: string,
    slug: string,
  },
  phone_number: string,
  rewards_type: string,
  score: number,
  score_entries: {
    description: number,
    goal_range: number,
    legal_entity_type: number,
    links: number,
    rewards_type: number,
    user: number,
  },
}

export const mainUrlsShape = PropTypes.shape({
  refresh_token: PropTypes.string,
  join: PropTypes.string,
  signin: PropTypes.string,
  signout: PropTypes.string,
  home: PropTypes.string,
  forbidden: PropTypes.string,
  discover: PropTypes.string,
  discussions: PropTypes.string,
  proposal: PropTypes.string,
  about: PropTypes.string,
  cookiePolicy: PropTypes.string,
  support: PropTypes.string,
  community: PropTypes.string,
  mobile: PropTypes.string,
  brandSiteUrl: langShape,
  okpalUrl: PropTypes.string,
})

export const locationShape = PropTypes.shape({
  hash: PropTypes.string,
  key: PropTypes.string,
  pathname: PropTypes.string,
  search: PropTypes.string,
})

export const historyShape = PropTypes.shape({
  action: PropTypes.string,
  block: PropTypes.func,
  createHref: PropTypes.func,
  go: PropTypes.func,
  goBack: PropTypes.func,
  goForward: PropTypes.func,
  length: PropTypes.number,
  listen: PropTypes.func,
  location: locationShape,
  push: PropTypes.func,
  replace: PropTypes.func,
})

export const matchShape = PropTypes.shape({
  isExact: PropTypes.bool,
  params: PropTypes.shape({
    filter: PropTypes.string,
    status: PropTypes.string,
  }),
  path: PropTypes.string,
  url: PropTypes.string,
})

export const imageShape = PropTypes.shape({
  '1x': PropTypes.string.isRequired,
  '2x': PropTypes.string.isRequired,
  '3x': PropTypes.string.isRequired,
})

export const dataUTMPageContextShape = PropTypes.shape({
  isFeaturedProject: PropTypes.bool,
  type: PropTypes.string.isRequired, // 'channel' | 'category'…
  value: PropTypes.string.isRequired, // 'Le titre de ma chaîne', 'La catégorie'…
})

export const orderShape = PropTypes.shape({
  id: PropTypes.number,
})
